import { useEffect, useCallback } from 'react';
import { cx } from '@linaria/core';
import { Group } from 'components/Group';
import { Row, Col } from 'components/Grid';
import { Button } from 'components/deprecated/Button';
import { textSmallBold, textRegularLinkLight } from 'styles/typography';
import cogoToast from 'components/ToastMessage/ToastMessage';
import { ReactLeft, reactLeftToJSX } from 'utils/uiStates/uiStates';
import { queryKeys, useQueryTE } from 'utils/react-query';
import { getSubscriptionTE } from 'api/users/subscription';
import { errorToReactLeft, pipe } from 'utils/fp';
import { either, taskEither } from 'fp-ts';
import { useQueryClient } from 'react-query';
import { useDataCollection } from 'modules/AnalyticsCollection/AnalyticsCollectionProvider';
import { Link } from 'components/Link';
import { IconLinkOpen } from 'components/Icons';
import { useCurrentUserId } from 'modules/Auth/hooks';
import { getPlansTE } from 'api/users/plans';

import { Form, Formik } from 'formik';
import { InputField } from 'components/Formik/InputField';
import { SelectField } from 'components/Formik/SelectField';
import { object, string } from 'yup';
import { sequenceT } from 'fp-ts/lib/Apply';
import { SelectOption } from 'components/Select';
import { alphabeticalSort, uniqueOptions } from 'utils/collections';
import { useUserSubscriptionMutation } from './hooks/useUserSubscriptionMutation';
import { subtitle, formGroup, formWrapper, flip90, linkWrapper, specialCaseInput } from './V2Api.styles';

const genericStringRules = string().required(' ');
const planCodeSchema = object().shape({
  'plan-code': genericStringRules,
});

type planCodeFields = {
  'plan-code': string;
};

const currentCodeSchema = object().shape({
  'current-plan': object().required(),
});

type currentCodeFields = {
  'current-plan': { value: string; name: string };
};

const planToOption = (plan: CF.API.Users.SubscriptionPlan): SelectOption => ({ name: plan.name, value: plan.id });

const uniqueSortedPlans = (plans: SelectOption[]) => pipe(plans, uniqueOptions, alphabeticalSort);

export const billingApiEffects = {
  getUserSubscriptionTE: (userId: string) =>
    pipe(
      getSubscriptionTE({ userId }, errorToReactLeft),
      taskEither.map(({ plan }) => plan),
    ),
  getPlansTE: (userId: string) =>
    pipe(
      getPlansTE({ userId }, errorToReactLeft),
      taskEither.map(({ plans }) => plans),
    ),
};

export const V2Api: React.FC = () => {
  const userId = useCurrentUserId();
  const queryClient = useQueryClient();
  const { data: userSubscriptionE } = useQueryTE([queryKeys.UserBilling, { userId }], billingApiEffects.getUserSubscriptionTE(userId));
  const { data: plansE } = useQueryTE([queryKeys.Plans, { userId }], billingApiEffects.getPlansTE(userId));
  const { track } = useDataCollection();

  useEffect(() => {
    queryClient.invalidateQueries([queryKeys.UserBilling, { userId }]);
  }, [userId]);

  const handleSuccess = useCallback(
    (message = 'Subscription updated!') =>
      (data: CF.API.Users.SubscriptionResponse) => {
        track('Billing Page -> Subscription Plan Updated', { message, plan: data.plan });
        cogoToast.success(message);
        queryClient.invalidateQueries([queryKeys.UserBilling, { userId }]);
      },
    [cogoToast, userId, queryClient],
  );

  const handleFailure = useCallback(
    (defaultMessage = 'Error.') =>
      (error: ReactLeft) => {
        track('Billing Page -> Subscription Plan Update Error', { message: error.props?.reason });
        cogoToast.error(error.props?.reason, { heading: defaultMessage });
      },
    [cogoToast],
  );

  const planMutation = useUserSubscriptionMutation({ userId, onSuccess: handleSuccess('Plan updated!'), onError: handleFailure() });
  const planCodeMutation = useUserSubscriptionMutation({ userId, onSuccess: handleSuccess('Plan code updated!'), onError: handleFailure() });

  return (
    <Group
      title="V2 API"
      headerActions={
        <Link href="https://clarifai.com/pricing" className={linkWrapper} target="_blank">
          <span className={textRegularLinkLight}>Learn about Plans features</span>
          <IconLinkOpen size={16} color="#195AFF" className={flip90} />
        </Link>
      }
    >
      {/* h3 might not be the right tag here. But the figma design creates confusion whether this is a heading or a label */}
      <h3 className={cx(textSmallBold, subtitle)}>Current Plan</h3>

      {pipe(
        sequenceT(either.Applicative)(userSubscriptionE, plansE),
        either.fold(reactLeftToJSX(), ([userSubscription, plans]) => {
          const currentPlan = planToOption(userSubscription);
          const dropdownPlansList = uniqueSortedPlans(plans.map(planToOption).concat(currentPlan));

          return (
            <>
              <Formik
                enableReinitialize
                initialValues={{
                  'current-plan': currentPlan,
                }}
                validationSchema={currentCodeSchema}
                onSubmit={async (values: currentCodeFields) => {
                  track('Billing Page -> Change Plan Clicked', { oldPlan: currentPlan, plan: values['current-plan'] });
                  return planMutation.mutateTE({ plan: { id: values['current-plan'].value } })();
                }}
              >
                {({ isSubmitting, dirty, isValid }) => (
                  <Form className={formWrapper}>
                    <Row>
                      <Col xs={12} md={6}>
                        <div className={formGroup}>
                          <div className="fullWidth">
                            <SelectField data-testid="billing-plans" name="current-plan" placeholder="Select a plan" options={dropdownPlansList} />
                          </div>
                          <div className="button-wrapper">
                            <Button kind="secondary" className="no-wrap" type="submit" disabled={!dirty || isSubmitting || !isValid}>
                              Change Plan
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
              <Formik
                initialValues={{
                  'plan-code': '',
                }}
                validationSchema={planCodeSchema}
                onSubmit={(values: planCodeFields) => {
                  return planCodeMutation.mutateTE({ signup_code: values['plan-code'] })();
                }}
              >
                {({ isSubmitting, isValid, dirty }) => (
                  <Form>
                    <Row>
                      <Col xs={12} md={6}>
                        <div className={formGroup}>
                          <div className="fullWidth">
                            <InputField className={specialCaseInput} name="plan-code" label="" placeholder="Enter a plan code" />
                          </div>
                          <div className="button-wrapper">
                            <Button kind="secondary" className="no-wrap" type="submit" disabled={isSubmitting || !isValid || !dirty}>
                              Use Plan Code
                            </Button>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Form>
                )}
              </Formik>
            </>
          );
        }),
      )}
    </Group>
  );
};
